/**
 * Health Records Component
 * Track vaccinations, vet visits, health tests, and medical history
 */

import { useState, useEffect } from 'react';
import { 
  Stethoscope,
  Syringe,
  TestTube,
  FileText,
  Plus,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  Edit,
  Trash2,
  Download,
  Bell
} from 'lucide-react';
import { Card, Button, Badge, Modal, Input, Select, Alert, Tabs } from '../shared/UIComponents';
import { formatDate, formatRelativeDate, cn } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { getCatsByCattery } from '../../firebase/firestore';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  getDocs,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';

// Health record types
const RECORD_TYPES = {
  vaccination: { 
    label: 'Vaccination', 
    icon: Syringe, 
    color: 'bg-blue-100 text-blue-700' 
  },
  vet_visit: { 
    label: 'Vet Visit', 
    icon: Stethoscope, 
    color: 'bg-purple-100 text-purple-700' 
  },
  test: { 
    label: 'Health Test', 
    icon: TestTube, 
    color: 'bg-teal-100 text-teal-700' 
  },
  medication: { 
    label: 'Medication', 
    icon: FileText, 
    color: 'bg-amber-100 text-amber-700' 
  },
  surgery: { 
    label: 'Surgery', 
    icon: Stethoscope, 
    color: 'bg-red-100 text-red-700' 
  }
};

// Common vaccinations
const VACCINATIONS = [
  { code: 'FVRCP', name: 'FVRCP (Core)', description: 'Feline Viral Rhinotracheitis, Calicivirus, Panleukopenia' },
  { code: 'RABIES', name: 'Rabies', description: 'Rabies vaccine' },
  { code: 'FELV', name: 'FeLV', description: 'Feline Leukemia Virus' },
  { code: 'FIV', name: 'FIV', description: 'Feline Immunodeficiency Virus' },
  { code: 'CHLAMYDIA', name: 'Chlamydia', description: 'Chlamydophila felis' },
  { code: 'BORDETELLA', name: 'Bordetella', description: 'Kennel cough' }
];

// Common health tests
const HEALTH_TESTS = [
  { code: 'HCM', name: 'HCM Screening', description: 'Hypertrophic Cardiomyopathy echo' },
  { code: 'PKD', name: 'PKD DNA Test', description: 'Polycystic Kidney Disease' },
  { code: 'FELV_FIV', name: 'FeLV/FIV Test', description: 'Leukemia & FIV screening' },
  { code: 'BLOODWORK', name: 'Blood Panel', description: 'Complete blood count & chemistry' },
  { code: 'FECAL', name: 'Fecal Test', description: 'Parasite screening' },
  { code: 'URINE', name: 'Urinalysis', description: 'Urine analysis' },
  { code: 'SMA', name: 'SMA DNA Test', description: 'Spinal Muscular Atrophy' },
  { code: 'PRA', name: 'PRA DNA Test', description: 'Progressive Retinal Atrophy' }
];

export const HealthRecords = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [records, setRecords] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedCat, setSelectedCat] = useState('');
  const [activeTab, setActiveTab] = useState('upcoming');

  useEffect(() => {
    if (user?.catteryId) {
      loadData();
    }
  }, [user?.catteryId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load cats
      const catsData = await getCatsByCattery(user.catteryId);
      setCats(catsData);
      
      // Load health records
      const recordsQuery = query(
        collection(db, 'health_records'),
        where('catteryId', '==', user.catteryId),
        orderBy('date', 'desc')
      );
      const recordsSnap = await getDocs(recordsQuery);
      setRecords(recordsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
    } catch (error) {
      console.error('Error loading health data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get upcoming vaccinations/reminders
  const getUpcomingReminders = () => {
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    return records
      .filter(r => r.nextDueDate)
      .map(r => ({
        ...r,
        nextDue: r.nextDueDate?.toDate ? r.nextDueDate.toDate() : new Date(r.nextDueDate)
      }))
      .filter(r => r.nextDue <= thirtyDaysFromNow && r.nextDue >= now)
      .sort((a, b) => a.nextDue - b.nextDue);
  };

  // Get overdue items
  const getOverdueItems = () => {
    const now = new Date();
    return records
      .filter(r => r.nextDueDate)
      .map(r => ({
        ...r,
        nextDue: r.nextDueDate?.toDate ? r.nextDueDate.toDate() : new Date(r.nextDueDate)
      }))
      .filter(r => r.nextDue < now)
      .sort((a, b) => a.nextDue - b.nextDue);
  };

  const upcomingReminders = getUpcomingReminders();
  const overdueItems = getOverdueItems();

  // Filter records by cat
  const filteredRecords = selectedCat 
    ? records.filter(r => r.catId === selectedCat)
    : records;

  const tabs = [
    { id: 'upcoming', label: 'Upcoming', count: upcomingReminders.length + overdueItems.length },
    { id: 'all', label: 'All Records', count: filteredRecords.length },
    { id: 'vaccinations', label: 'Vaccinations' },
    { id: 'tests', label: 'Health Tests' }
  ];

  const getCatName = (catId) => cats.find(c => c.id === catId)?.name || 'Unknown';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">Health Records</h1>
          <p className="text-gray-500">Track vaccinations, vet visits, and health tests</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Plus className="w-4 h-4" />
          Add Record
        </Button>
      </div>

      {/* Alerts */}
      {overdueItems.length > 0 && (
        <Alert type="error" title={`${overdueItems.length} Overdue Item${overdueItems.length > 1 ? 's' : ''}`}>
          <p className="text-sm">
            You have overdue vaccinations or health checks. Please schedule appointments ASAP.
          </p>
        </Alert>
      )}

      {upcomingReminders.length > 0 && overdueItems.length === 0 && (
        <Alert type="warning" title="Upcoming Reminders">
          <p className="text-sm">
            {upcomingReminders.length} vaccination{upcomingReminders.length > 1 ? 's' : ''} or 
            health check{upcomingReminders.length > 1 ? 's' : ''} due in the next 30 days.
          </p>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <Syringe className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Vaccinations</p>
            <p className="text-2xl font-bold text-gray-900">
              {records.filter(r => r.type === 'vaccination').length}
            </p>
          </div>
        </Card>

        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <Stethoscope className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Vet Visits</p>
            <p className="text-2xl font-bold text-gray-900">
              {records.filter(r => r.type === 'vet_visit').length}
            </p>
          </div>
        </Card>

        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
            <TestTube className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Health Tests</p>
            <p className="text-2xl font-bold text-gray-900">
              {records.filter(r => r.type === 'test').length}
            </p>
          </div>
        </Card>

        <Card className={cn(
          "flex items-center gap-4",
          overdueItems.length > 0 ? "ring-2 ring-red-500" : ""
        )}>
          <div className={cn(
            "w-12 h-12 rounded-xl flex items-center justify-center",
            overdueItems.length > 0 ? "bg-red-100" : "bg-paw-100"
          )}>
            <Bell className={cn(
              "w-6 h-6",
              overdueItems.length > 0 ? "text-red-600" : "text-paw-600"
            )} />
          </div>
          <div>
            <p className="text-sm text-gray-500">Reminders</p>
            <p className={cn(
              "text-2xl font-bold",
              overdueItems.length > 0 ? "text-red-600" : "text-gray-900"
            )}>
              {overdueItems.length + upcomingReminders.length}
            </p>
          </div>
        </Card>
      </div>

      {/* Cat Filter */}
      <div className="flex items-center gap-4">
        <Select
          value={selectedCat}
          onChange={(e) => setSelectedCat(e.target.value)}
          options={cats.map(c => ({ value: c.id, label: c.name }))}
          placeholder="All Cats"
          className="w-48"
        />
      </div>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Tab Content */}
      {activeTab === 'upcoming' && (
        <UpcomingReminders 
          overdue={overdueItems}
          upcoming={upcomingReminders}
          getCatName={getCatName}
          onEdit={(record) => {
            setSelectedRecord(record);
            setShowModal(true);
          }}
        />
      )}

      {activeTab === 'all' && (
        <RecordsList 
          records={filteredRecords}
          loading={loading}
          getCatName={getCatName}
          onEdit={(record) => {
            setSelectedRecord(record);
            setShowModal(true);
          }}
          onDelete={async (id) => {
            if (confirm('Delete this record?')) {
              await deleteDoc(doc(db, 'health_records', id));
              loadData();
            }
          }}
        />
      )}

      {activeTab === 'vaccinations' && (
        <VaccinationSchedule 
          records={filteredRecords.filter(r => r.type === 'vaccination')}
          cats={selectedCat ? cats.filter(c => c.id === selectedCat) : cats}
          getCatName={getCatName}
        />
      )}

      {activeTab === 'tests' && (
        <HealthTestsSummary 
          records={filteredRecords.filter(r => r.type === 'test')}
          cats={selectedCat ? cats.filter(c => c.id === selectedCat) : cats}
          getCatName={getCatName}
        />
      )}

      {/* Add/Edit Modal */}
      <HealthRecordModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
        cats={cats}
        catteryId={user?.catteryId}
        onSuccess={() => {
          setShowModal(false);
          setSelectedRecord(null);
          loadData();
        }}
      />
    </div>
  );
};

// Upcoming Reminders
const UpcomingReminders = ({ overdue, upcoming, getCatName, onEdit }) => {
  if (overdue.length === 0 && upcoming.length === 0) {
    return (
      <Card className="text-center py-12">
        <CheckCircle className="w-12 h-12 text-paw-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">All Up to Date!</h3>
        <p className="text-gray-500">No vaccinations or health checks due in the next 30 days.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overdue */}
      {overdue.length > 0 && (
        <div>
          <h3 className="font-semibold text-red-600 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Overdue ({overdue.length})
          </h3>
          <div className="space-y-2">
            {overdue.map((record) => (
              <ReminderCard 
                key={record.id} 
                record={record} 
                getCatName={getCatName}
                isOverdue={true}
                onEdit={onEdit}
              />
            ))}
          </div>
        </div>
      )}

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <div>
          <h3 className="font-semibold text-amber-600 mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Due Soon ({upcoming.length})
          </h3>
          <div className="space-y-2">
            {upcoming.map((record) => (
              <ReminderCard 
                key={record.id} 
                record={record} 
                getCatName={getCatName}
                isOverdue={false}
                onEdit={onEdit}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Reminder Card
const ReminderCard = ({ record, getCatName, isOverdue, onEdit }) => {
  const config = RECORD_TYPES[record.type];
  const Icon = config?.icon || FileText;

  return (
    <Card className={cn(
      "flex items-center gap-4",
      isOverdue ? "border-l-4 border-red-500" : "border-l-4 border-amber-500"
    )}>
      <div className={cn(
        'w-12 h-12 rounded-xl flex items-center justify-center',
        config?.color
      )}>
        <Icon className="w-6 h-6" />
      </div>
      
      <div className="flex-1">
        <p className="font-semibold text-gray-900">{record.name || config?.label}</p>
        <p className="text-sm text-gray-500">
          {getCatName(record.catId)} • Due: {formatDate(record.nextDue)}
        </p>
      </div>
      
      <Badge variant={isOverdue ? 'danger' : 'warning'}>
        {isOverdue ? 'Overdue' : formatRelativeDate(record.nextDue)}
      </Badge>
      
      <Button variant="ghost" size="sm" onClick={() => onEdit(record)}>
        <Edit className="w-4 h-4" />
      </Button>
    </Card>
  );
};

// Records List
const RecordsList = ({ records, loading, getCatName, onEdit, onDelete }) => {
  if (loading) {
    return <Card><div className="animate-pulse space-y-4">
      {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-lg" />)}
    </div></Card>;
  }

  if (records.length === 0) {
    return (
      <Card className="text-center py-12">
        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No health records yet</p>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden p-0">
      <div className="divide-y divide-gray-200">
        {records.map((record) => {
          const config = RECORD_TYPES[record.type];
          const Icon = config?.icon || FileText;
          
          return (
            <div key={record.id} className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-4">
              <div className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                config?.color
              )}>
                <Icon className="w-5 h-5" />
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">{record.name}</p>
                <p className="text-sm text-gray-500">
                  {getCatName(record.catId)} • {formatDate(record.date)}
                  {record.vetClinic && ` • ${record.vetClinic}`}
                </p>
              </div>
              
              {record.result && (
                <Badge variant={record.result === 'negative' || record.result === 'normal' ? 'success' : 'warning'}>
                  {record.result}
                </Badge>
              )}
              
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onEdit(record)}
                  className="p-2 text-gray-400 hover:text-feline-600 rounded-lg"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(record.id)}
                  className="p-2 text-gray-400 hover:text-red-600 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// Vaccination Schedule Grid
const VaccinationSchedule = ({ records, cats, getCatName }) => {
  return (
    <Card>
      <h3 className="font-semibold text-gray-900 mb-4">Vaccination Status by Cat</h3>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Cat</th>
              {VACCINATIONS.slice(0, 4).map(v => (
                <th key={v.code} className="px-4 py-3 text-center text-xs font-semibold text-gray-500">
                  {v.code}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {cats.map((cat) => {
              const catRecords = records.filter(r => r.catId === cat.id);
              
              return (
                <tr key={cat.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{cat.name}</td>
                  {VACCINATIONS.slice(0, 4).map(v => {
                    const vaccRecord = catRecords.find(r => r.vaccinationType === v.code);
                    const isUpToDate = vaccRecord && vaccRecord.nextDueDate && 
                      (vaccRecord.nextDueDate.toDate ? vaccRecord.nextDueDate.toDate() : new Date(vaccRecord.nextDueDate)) > new Date();
                    
                    return (
                      <td key={v.code} className="px-4 py-3 text-center">
                        {vaccRecord ? (
                          <span className={cn(
                            'inline-flex items-center justify-center w-6 h-6 rounded-full text-xs',
                            isUpToDate ? 'bg-paw-100 text-paw-600' : 'bg-amber-100 text-amber-600'
                          )}>
                            {isUpToDate ? '✓' : '!'}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// Health Tests Summary
const HealthTestsSummary = ({ records, cats, getCatName }) => {
  return (
    <Card>
      <h3 className="font-semibold text-gray-900 mb-4">Health Test Results</h3>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Cat</th>
              {HEALTH_TESTS.slice(0, 4).map(t => (
                <th key={t.code} className="px-4 py-3 text-center text-xs font-semibold text-gray-500">
                  {t.code}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {cats.map((cat) => {
              const catRecords = records.filter(r => r.catId === cat.id);
              
              return (
                <tr key={cat.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{cat.name}</td>
                  {HEALTH_TESTS.slice(0, 4).map(t => {
                    const testRecord = catRecords.find(r => r.testType === t.code);
                    
                    return (
                      <td key={t.code} className="px-4 py-3 text-center">
                        {testRecord ? (
                          <Badge variant={
                            testRecord.result === 'negative' || testRecord.result === 'normal' 
                              ? 'success' 
                              : testRecord.result === 'positive' 
                                ? 'danger' 
                                : 'warning'
                          }>
                            {testRecord.result}
                          </Badge>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// Health Record Modal
const HealthRecordModal = ({ isOpen, onClose, record, cats, catteryId, onSuccess }) => {
  const [formData, setFormData] = useState({
    type: 'vaccination',
    catId: '',
    name: '',
    date: new Date().toISOString().split('T')[0],
    nextDueDate: '',
    vaccinationType: '',
    testType: '',
    result: '',
    vetClinic: '',
    vetName: '',
    cost: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (record) {
      setFormData({
        type: record.type || 'vaccination',
        catId: record.catId || '',
        name: record.name || '',
        date: record.date ? formatDateForInput(record.date) : new Date().toISOString().split('T')[0],
        nextDueDate: record.nextDueDate ? formatDateForInput(record.nextDueDate) : '',
        vaccinationType: record.vaccinationType || '',
        testType: record.testType || '',
        result: record.result || '',
        vetClinic: record.vetClinic || '',
        vetName: record.vetName || '',
        cost: record.cost || '',
        notes: record.notes || ''
      });
    } else {
      setFormData({
        type: 'vaccination',
        catId: '',
        name: '',
        date: new Date().toISOString().split('T')[0],
        nextDueDate: '',
        vaccinationType: '',
        testType: '',
        result: '',
        vetClinic: '',
        vetName: '',
        cost: '',
        notes: ''
      });
    }
  }, [record]);

  const formatDateForInput = (date) => {
    const d = date?.toDate ? date.toDate() : new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleSubmit = async () => {
    if (!formData.catId || !formData.type) return;

    try {
      setSubmitting(true);
      
      const data = {
        type: formData.type,
        catId: formData.catId,
        name: formData.name,
        date: Timestamp.fromDate(new Date(formData.date)),
        nextDueDate: formData.nextDueDate ? Timestamp.fromDate(new Date(formData.nextDueDate)) : null,
        vaccinationType: formData.vaccinationType || null,
        testType: formData.testType || null,
        result: formData.result || null,
        vetClinic: formData.vetClinic,
        vetName: formData.vetName,
        cost: formData.cost ? Number(formData.cost) : null,
        notes: formData.notes,
        catteryId,
        updatedAt: serverTimestamp()
      };

      if (record?.id) {
        await updateDoc(doc(db, 'health_records', record.id), data);
      } else {
        data.createdAt = serverTimestamp();
        await addDoc(collection(db, 'health_records'), data);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving health record:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const typeOptions = Object.entries(RECORD_TYPES).map(([key, val]) => ({
    value: key, label: val.label
  }));

  const catOptions = cats.map(c => ({ value: c.id, label: c.name }));
  const vaccOptions = VACCINATIONS.map(v => ({ value: v.code, label: v.name }));
  const testOptions = HEALTH_TESTS.map(t => ({ value: t.code, label: t.name }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={record ? 'Edit Health Record' : 'Add Health Record'} size="md">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Record Type *"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            options={typeOptions}
          />
          <Select
            label="Cat *"
            value={formData.catId}
            onChange={(e) => setFormData({ ...formData, catId: e.target.value })}
            options={catOptions}
            placeholder="Select cat"
          />
        </div>

        {formData.type === 'vaccination' && (
          <Select
            label="Vaccination Type"
            value={formData.vaccinationType}
            onChange={(e) => setFormData({ ...formData, vaccinationType: e.target.value, name: VACCINATIONS.find(v => v.code === e.target.value)?.name || '' })}
            options={vaccOptions}
            placeholder="Select vaccination"
          />
        )}

        {formData.type === 'test' && (
          <>
            <Select
              label="Test Type"
              value={formData.testType}
              onChange={(e) => setFormData({ ...formData, testType: e.target.value, name: HEALTH_TESTS.find(t => t.code === e.target.value)?.name || '' })}
              options={testOptions}
              placeholder="Select test"
            />
            <Select
              label="Result"
              value={formData.result}
              onChange={(e) => setFormData({ ...formData, result: e.target.value })}
              options={[
                { value: 'negative', label: 'Negative' },
                { value: 'positive', label: 'Positive' },
                { value: 'normal', label: 'Normal' },
                { value: 'abnormal', label: 'Abnormal' },
                { value: 'pending', label: 'Pending' }
              ]}
              placeholder="Select result"
            />
          </>
        )}

        <Input
          label="Name/Description"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Annual FVRCP booster"
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Date *"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          />
          <Input
            label="Next Due Date"
            type="date"
            value={formData.nextDueDate}
            onChange={(e) => setFormData({ ...formData, nextDueDate: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Vet Clinic"
            value={formData.vetClinic}
            onChange={(e) => setFormData({ ...formData, vetClinic: e.target.value })}
            placeholder="Clinic name"
          />
          <Input
            label="Cost (ZAR)"
            type="number"
            value={formData.cost}
            onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
            placeholder="0.00"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={submitting}>
            {record ? 'Update' : 'Add'} Record
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default HealthRecords;
